/* BIOVAQ hero slideshow (sections/biovaq-hero.liquid): fade autoplay, dots, swipe, keys, pause. */
if (!customElements.get('biovaq-hero')) {
  class BiovaqHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.biovaq-hero__slide'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('.biovaq-hero__dot'));
      this.pauseButton = this.querySelector('[data-pause]');
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.dataset.autoplay !== 'true' || reduceMotion;
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go))));
      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.updatePauseButton();
          this.schedule();
        });
      }

      // Pause only for a real mouse hover and for keyboard focus. A tap on a phone fires an emulated mouseenter (and
      // Android, like a desktop click, focuses the tapped dot) with no leave until the next tap elsewhere, which
      // stopped autoplay for good after the first touch or dot click (BIOVAQ, 2026-10-09).
      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.hold('hovered', true));
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.hold('hovered', false));
      this.addEventListener('focusin', (event) => this.isKeyboardFocus(event.target) && this.hold('focused', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focused', false);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.addEventListener('touchstart', (event) => (this.touchX = event.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (event) => {
          const dx = event.changedTouches[0].clientX - (this.touchX ?? event.changedTouches[0].clientX);
          if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        },
        { passive: true }
      );

      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the block being edited and stop rotating while it is selected.
      this.onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        this.editorHold = true;
        this.go(this.slides.indexOf(event.target));
      };
      this.onBlockDeselect = (event) => {
        if (!this.contains(event.target)) return;
        this.editorHold = false;
        this.schedule();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.updatePauseButton();
      this.queueHydration();
      this.schedule();
    }

    // Slides 2..n ship their image URLs in data-srcset/data-src (sections/biovaq-hero.liquid) so they don't compete
    // with the slide-1 LCP image. Fill them in once slide 1 has loaded (or on window load, whichever comes first).
    queueHydration() {
      const start = () => {
        if (this.hydrationQueued) return;
        this.hydrationQueued = true;
        const run = () => this.slides.slice(1).forEach((slide) => this.hydrate(slide));
        if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1000 });
        else setTimeout(run, 200);
      };
      const first = this.slides[0].querySelector('img');
      if ((window.Shopify && window.Shopify.designMode) || !first || first.complete) {
        start();
        return;
      }
      first.addEventListener('load', start, { once: true });
      first.addEventListener('error', start, { once: true });
      window.addEventListener('load', start, { once: true });
    }

    hydrate(slide) {
      slide.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });
      const img = slide.querySelector('img');
      if (img && (img.dataset.srcset || img.dataset.src)) {
        img.loading = 'eager';
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      }
      return img;
    }

    // Resolves once the slide's image can be painted, so a fade never reveals an empty slide.
    whenReady(slide) {
      const img = this.hydrate(slide);
      if (!img || !img.getAttribute('src')) return Promise.resolve();
      if (!img.decode) {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve, reject) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', reject, { once: true });
        });
      }
      return img.decode().catch((error) => {
        if (!(img.complete && img.naturalWidth)) throw error;
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    hold(reason, value) {
      this[reason] = value;
      this.schedule();
    }

    isKeyboardFocus(element) {
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return true; // no :focus-visible support: keep the old pause-on-any-focus behaviour
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.userPaused || this.hovered || this.focused || this.editorHold || document.hidden) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
    }

    go(target) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      if (next === this.index) {
        this.pending = null;
        this.schedule();
        return;
      }
      // Wait for the incoming image before fading; the latest request wins. If it fails, stay on the current
      // slide and let the next autoplay tick try again.
      this.pending = next;
      clearTimeout(this.timer);
      this.whenReady(this.slides[next]).then(
        () => {
          if (this.pending === next) this.show(next);
        },
        () => {
          if (this.pending !== next) return;
          this.pending = null;
          this.schedule();
        }
      );
    }

    show(next) {
      this.pending = null;
      if (next !== this.index) {
        const current = this.slides[this.index];
        current.classList.remove('is-active');
        current.setAttribute('aria-hidden', 'true');
        current.inert = true;

        const incoming = this.slides[next];
        incoming.classList.add('is-active');
        incoming.removeAttribute('aria-hidden');
        incoming.inert = false;

        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === next);
          if (i === next) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.index = next;
      }
      this.schedule();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.querySelector('[data-pause-label]').textContent = this.userPaused
        ? 'Play slideshow'
        : 'Pause slideshow';
    }
  }

  customElements.define('biovaq-hero', BiovaqHero);
}
